import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TopicParagraphMapping } from '@src/utility/story';

/**
 * Where a topic is *spoken* in the source video — the bridge between the
 * story's topics and a clip, which records a source range and nothing about
 * what it is about.
 *
 * A clip carries `clipJson.theme`, which reads like a topic and is not one: it
 * is the **prompt** the clip was produced from ("Custom", a preset title), which
 * is why matching a story topic against it found nothing. The app's one record
 * of which part of the story is about which topic is the topic→paragraph
 * mapping the Transcript & Speakers tab computes and persists on the story
 * (`aiResponse.topicParagraphMapping`). Turning those paragraphs into times
 * makes it answerable for a clip: a clip is about a topic when it plays a
 * passage tagged with it.
 *
 * Pure, and in the shape of `clipPickerFilter.ts` — the rule is provable
 * without a store, a fetch or a rendered modal.
 */

/** A stretch of the source video, in seconds. */
export type TimeSpan = { start: number; end: number };

/** Topic title → the spans of source the topic covers, in order, merged. */
export type TopicSpans = ReadonlyMap<string, readonly TimeSpan[]>;

/**
 * How much of a clip has to fall inside a topic's span to count. A quarter of
 * a second is the same tolerance the picker's already-in-the-reel check uses:
 * above the frame-snapping a crop applies, below the shortest passage anyone
 * saves. Without it a clip that merely butts up against a tagged paragraph —
 * or overshoots it by a frame — would read as being about that topic.
 */
const MIN_OVERLAP_SECONDS = 0.25;

/** Whether two spans share more than a boundary's worth of time. */
export function spansOverlap(a: TimeSpan, b: TimeSpan): boolean {
  return (
    Math.min(a.end, b.end) - Math.max(a.start, b.start) > MIN_OVERLAP_SECONDS
  );
}

/**
 * Each paragraph's extent in the source: first word in to last word out.
 *
 * One pass over the transcript. Elements with no timing are skipped rather than
 * treated as zero — a removed element's `ts` is `null` at runtime despite the
 * type (see `docs/transcript-ui-patterns.md`), and folding that into a span
 * would stretch it back to the start of the video.
 */
function paragraphSpans(
  elements: readonly TranscriptElement[],
): Map<string, TimeSpan> {
  const spans = new Map<string, TimeSpan>();

  for (const element of elements) {
    if (element.type !== 'text') continue;
    const paragraphId = element.paragraph_id;
    if (!paragraphId) continue;

    const start = element.ts;
    if (typeof start !== 'number' || !Number.isFinite(start)) continue;
    const end =
      typeof element.end_ts === 'number' && Number.isFinite(element.end_ts)
        ? element.end_ts
        : start;

    const existing = spans.get(paragraphId);
    if (existing) {
      existing.start = Math.min(existing.start, start);
      existing.end = Math.max(existing.end, end);
    } else {
      spans.set(paragraphId, { start, end });
    }
  }

  return spans;
}

/**
 * Merges spans that touch or overlap, so a topic that owns six consecutive
 * paragraphs is one span rather than six. The clip test below runs per visible
 * row per keystroke; this is what keeps it short.
 */
function mergeSpans(spans: TimeSpan[]): TimeSpan[] {
  if (spans.length < 2) return spans;

  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const merged: TimeSpan[] = [sorted[0]];

  for (const span of sorted.slice(1)) {
    const last = merged[merged.length - 1];
    if (span.start <= last.end) {
      last.end = Math.max(last.end, span.end);
    } else {
      merged.push(span);
    }
  }

  return merged;
}

/**
 * The story's topics as source spans. Empty when there is no mapping yet —
 * which is what the picker's topic control stands down on, rather than offering
 * a filter that can only answer "no clips".
 */
export function topicSpansFromMapping(
  mapping: TopicParagraphMapping | null | undefined,
  elements: readonly TranscriptElement[] | undefined,
): TopicSpans {
  const byTopic = new Map<string, readonly TimeSpan[]>();
  if (!mapping || !elements?.length) return byTopic;

  const spansByParagraph = paragraphSpans(elements);

  for (const [topic, paragraphIds] of Object.entries(mapping.byTopic)) {
    const spans = paragraphIds
      .map(paragraphId => spansByParagraph.get(paragraphId))
      .filter((span): span is TimeSpan => !!span)
      // Copied on merge, so the paragraph map's own spans are never mutated by
      // a second topic that shares a paragraph.
      .map(span => ({ ...span }));
    if (spans.length > 0) byTopic.set(topic, mergeSpans(spans));
  }

  return byTopic;
}

/** The spans covered by *any* of the chosen topics — the filter is OR within itself. */
export function spansForTopics(
  topicSpans: TopicSpans | undefined,
  topics: readonly string[],
): TimeSpan[] {
  if (!topicSpans || topics.length === 0) return [];
  return mergeSpans(
    topics.flatMap(topic =>
      (topicSpans.get(topic) ?? []).map(span => ({ ...span })),
    ),
  );
}
